import { toast } from "@win-reality/react-ui";
import clsx from "clsx";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useInterruptedTagging } from "../../../hooks/useInterruptedTagging";
import { selectFocusedEvent } from "../../../store/slices/socket/selectors";
import { setModalState } from "../../../store/slices/view";
import type { RootState } from "../../../store/store";
import { ActivePlayerTile } from "../../shared/ActivePlayerTile";
import { TilesContainer } from "../../shared/TilesContainer";
import { VisualizationContainer } from "../../shared/VisualizationContainer";
import { BottomButtons } from "./BottomButtons";
import { EventQueue } from "./EventQueue";
import { TaggingSteps } from "./TaggingSteps";

/**
 * GameView - Live game tagging interface
 *
 * This is the main view for tagging live games. It displays:
 * - Active player tiles (hitter, pitcher, catcher)
 * - Data tiles (pitch metrics, hit metrics)
 * - Tagging steps (pitch type, result, etc.)
 * - Event queue (pitch history)
 * - Action buttons (submit, reset, etc.)
 *
 * Phase 2: Core layout with placeholders
 * Components will be replaced with real implementations in later phases
 */
export const GameView = () => {
  const dispatch = useDispatch();

  // Detect when a new event arrives while user is tagging, preserving their work
  useInterruptedTagging();

  // Redux state selectors
  const gameState = useSelector((state: RootState) => state.game.gameState);
  const away = useSelector((state: RootState) => state.game.rosters.away);
  const home = useSelector((state: RootState) => state.game.rosters.home);
  const { isViewingPreviousEvent } = useSelector(
    (state: RootState) => state.socket,
  );
  const focusedEvent = useSelector(selectFocusedEvent);

  const { focusedPlayers } = focusedEvent;
  const { inning } = gameState;

  // Calculate current players based on inning and viewing state
  const pitchCount = inning.half === "top" ? home.pitchCount : away.pitchCount;

  const currentPitcher = isViewingPreviousEvent
    ? focusedPlayers?.focusedPitcher
    : inning.half === "top"
      ? home.currentPitcher
      : away.currentPitcher;

  const currentHitter = isViewingPreviousEvent
    ? focusedPlayers?.focusedHitter
    : inning.half === "top"
      ? away.currentHitter
      : home.currentHitter;

  const currentCatcher = isViewingPreviousEvent
    ? focusedPlayers?.focusedCatcher
    : inning.half === "top"
      ? home.currentCatcher
      : away.currentCatcher;

  // Show welcome tip on mount
  useEffect(() => {
    toast("PRO TIP: Zoom in/out to adjust Dashboard to fit your screen size!", {
      variant: "info",
      position: "top-center",
    });
  }, []);

  // Modal handlers (will be used when ActivePlayerTile is implemented)
  const handleHitterClick = () => {
    dispatch(setModalState({ state: "Lineup Swap", data: {} }));
  };

  const handlePitcherClick = () => {
    dispatch(setModalState({ state: "Pitcher Sub", data: {} }));
  };

  const handleCatcherClick = () => {
    dispatch(setModalState({ state: "Catcher Sub", data: {} }));
  };

  return (
    <div
      className={clsx(
        // Size
        "min-h-[calc(100vh-var(--navbar-height))]",
        // Layout
        "flex flex-col items-center justify-around",
        // Spacing
        "gap-4 px-6 sm:px-4 py-4",
      )}
    >
      {/* Row 1: Active Players */}
      <div className="flex w-full justify-center gap-4 flex-wrap">
        <ActivePlayerTile
          playerType="hitter"
          player={currentHitter}
          handleClick={handleHitterClick}
          clickable
        />

        <ActivePlayerTile
          playerType="pitcher"
          player={currentPitcher}
          pitchCount={pitchCount}
          handleClick={handlePitcherClick}
          clickable
        />

        <ActivePlayerTile
          playerType="catcher"
          player={currentCatcher}
          handleClick={handleCatcherClick}
          clickable
        />
      </div>

      {/* Row 2: Data Tiles */}
      <TilesContainer direction="row" tileType="game" />

      {/* Row 3: Main Tagging Area */}
      <div
        className={clsx(
          // Size
          "w-full",
          // Layout
          "grid grid-cols-1 lg:grid-cols-[360px_1fr] xl:grid-cols-[360px_minmax(385px,1fr)_minmax(280px,1fr)]",
          // Spacing
          "gap-4",
        )}
      >
        {/* Visualization Container - Strike Zone and other visualizations */}
        <VisualizationContainer withHittingOptions={true} />

        {/* TaggingSteps - Vertical tab interface for tagging workflow */}
        <div>
          <TaggingSteps />
        </div>

        {/* Event Queue - Current and previous events */}
        <div className="lg:col-span-2 xl:col-span-1">
          <EventQueue />
        </div>
      </div>

      {/* Row 4: Bottom Buttons */}
      <BottomButtons />
    </div>
  );
};
